import React, { useState, useEffect, useRef } from 'react';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faGraduationCap,
  faTimes,
  faBars,
  faUser,
  faSignInAlt,
  faSignOutAlt,
  faChevronDown,
  faChevronUp,
  faUserCircle,
  faUserShield,
  faBookOpen,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';
import { useSidebar } from '../../../context/SidebarContext';
import { useAuthContext } from '../../../context/Context';
import { deleteUserFromLocalStorage } from '../../../services/auth.util';

const GlobalSidebar = () => {
  const { 
    isCollapsed, 
    isMobile, 
    isMobileSidebarVisible, 
    collapseSidebar, 
    expandSidebar, 
    hideMobileSidebar 
  } = useSidebar();
  const { loggedIn, loggedInUser, setLoggedIn, setLoggedInUser } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  
  // User menu state
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const sidebarRef = useRef(null);

  // Handle clicks outside sidebar and user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user menu when clicking outside
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }

      // Handle sidebar clicks outside
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (isMobile && isMobileSidebarVisible) {
          // Hide mobile sidebar when clicking outside
          hideMobileSidebar();
        } else if (!isMobile && !isCollapsed) {
          // Collapse desktop sidebar when clicking outside (only if expanded)
          collapseSidebar();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCollapsed, isMobile, isMobileSidebarVisible, collapseSidebar, hideMobileSidebar]);

  // Handle sidebar click to expand when collapsed
  const handleSidebarClick = (event) => {
    // Only expand if sidebar is collapsed and not on mobile
    if (!isMobile && isCollapsed) {
      expandSidebar();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Generate user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle user logout
  const handleLogout = () => {
    deleteUserFromLocalStorage();
    setLoggedIn(false);
    setLoggedInUser(null);
    setIsUserMenuOpen(false);
    if (isMobile) {
      hideMobileSidebar();
    }
    navigate('/');
  };

  // Handle navigation to auth pages
  const handleLogin = () => {
    setIsUserMenuOpen(false);
    if (isMobile) {
      hideMobileSidebar();
    }
    navigate('/login');
  };

  const handleProfile = () => {
    setIsUserMenuOpen(false);
    if (isMobile) {
      hideMobileSidebar();
    }
    navigate('/user-settings');
  };



  // User Menu Component
  const UserMenu = () => {
    if (loggedIn && loggedInUser) {
      // Authenticated user menu
      return (
        <div 
          ref={userMenuRef}
          style={{
            position: 'relative',
            margin: (isMobile || !isCollapsed) ? '20px 15px' : '10px 0',
            borderTop: '1px solid #333333',
            paddingTop: '15px'
          }}
        >
          {/* User Profile Button */}
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              padding: (isMobile || !isCollapsed) ? '12px 15px' : '10px',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: (isMobile || !isCollapsed) ? 'space-between' : 'center',
              width: '100%',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(15, 156, 243, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            aria-label="User menu"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* User Avatar */}
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#333333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#ffffff',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {loggedInUser.profileImage ? (
                  <img 
                    src={loggedInUser.profileImage} 
                    alt={loggedInUser.fullName || loggedInUser.firstName || 'User'} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  getUserInitials(loggedInUser.fullName || loggedInUser.firstName || loggedInUser.email)
                )}
                {/* Online indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '2px',
                  right: '2px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#4ade80',
                  borderRadius: '50%',
                  border: '2px solid #000000'
                }}></div>
                {/* Admin indicator (when sidebar collapsed) */}
                {loggedInUser.role === 'admin' && (
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '10px',
                    height: '10px',
                    backgroundColor: 'rgba(251, 191, 36, 0.9)',
                    borderRadius: '50%',
                    border: '1px solid #000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '7px',
                    fontWeight: '700',
                    color: '#1f2937',
                    lineHeight: 1
                  }} title="Administrator">A</div>
                )}
              </div>
              
              {(isMobile || !isCollapsed) && (
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {loggedInUser.fullName || loggedInUser.firstName || 'User'}
                    {loggedInUser.role === 'admin' && (
                      <span
                        style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          padding: '1px 4px',
                          borderRadius: '2px',
                          backgroundColor: 'rgba(251, 191, 36, 0.2)',
                          color: '#fbbf24',
                          textTransform: 'uppercase',
                          letterSpacing: '0.6px',
                          border: '1px solid rgba(251, 191, 36, 0.4)'
                        }}
                        title="Administrator"
                      >
                        Admin
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>
                    {loggedInUser.email}
                  </div>
                </div>
              )}
            </div>
            
            {(isMobile || !isCollapsed) && (
              <FontAwesomeIcon 
                icon={isUserMenuOpen ? faChevronUp : faChevronDown} 
                style={{ fontSize: '12px', opacity: 0.7 }}
              />
            )}
          </button>

          {/* Dropdown Menu */}
          {isUserMenuOpen && (
            <div style={{
              position: (!isMobile && isCollapsed) ? 'absolute' : 'static',
              left: (!isMobile && isCollapsed) ? '60px' : 'auto',
              top: (!isMobile && isCollapsed) ? '0' : 'auto',
              backgroundColor: '#111111',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              padding: '8px',
              marginTop: (!isMobile && isCollapsed) ? '0' : '8px',
              minWidth: (!isMobile && isCollapsed) ? '200px' : 'auto',
              zIndex: 1000,
              border: '1px solid #333333',
              animation: 'fadeIn 0.2s ease-out',
            }}>
              {/* Profile Link */}
              <button
                onClick={handleProfile}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(15, 156, 243, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon icon={faUser} style={{ fontSize: '14px' }} />
                My Profile
              </button>

              {/* Divider */}
              <div style={{
                height: '1px',
                backgroundColor: '#2a2a4a',
                margin: '8px 0'
              }}></div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 107, 107, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '14px' }} />
                Sign Out
              </button>
            </div>
          )}
        </div>
      );
    } else {
      // Unauthenticated user menu
      return (
        <div style={{
          margin: (isMobile || !isCollapsed) ? '20px 15px' : '10px 0',
          borderTop: '1px solid #333333',
          paddingTop: '15px'
        }}>
          {(isMobile || !isCollapsed) ? (
            <div style={{ marginBottom: '15px' }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#a0a0a0', 
                marginBottom: '15px',
                textAlign: 'center'
              }}>
                Join gvkEDU
              </div>
              
              {/* Login Button */}
              <button
                onClick={handleLogin}
                style={{
                  background: 'linear-gradient(135deg, #0f9cf3 0%, #0d7bc7 100%)',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '10px',
                  boxShadow: '0 4px 15px rgba(15, 156, 243, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(15, 156, 243, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(15, 156, 243, 0.3)';
                }}
              >
                <FontAwesomeIcon icon={faSignInAlt} style={{ fontSize: '14px' }} />
                Sign In
              </button>

            </div>
          ) : (
            // Collapsed view - just show login icon
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleLogin}
                style={{
                  background: '#0f9cf3',
                  border: 'none',
                  color: '#ffffff',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  margin: '0 auto'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.1)';
                  e.target.style.boxShadow = '0 4px 15px rgba(15, 156, 243, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
                title="Sign In"
              >
                <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '18px' }} />
              </button>
            </div>
          )}
        </div>
      );
    }
  };

  // Don't render sidebar on mobile unless explicitly shown
  if (isMobile && !isMobileSidebarVisible) {
    return null;
  }

  // Don't render sidebar on desktop if user is not logged in
  if (!isMobile && !loggedIn) {
    return null;
  }

  return (
    <>
      {/* Add CSS for animations and mobile styles */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          
          /* Hide sidebar on small screens unless explicitly shown */
          @media (max-width: 768px) {
            .global-sidebar-wrapper {
              display: ${isMobile && isMobileSidebarVisible ? 'block' : 'none'};
            }
          }
          
          /* Prevent body scroll when mobile sidebar is open */
          ${isMobile && isMobileSidebarVisible ? `
            body {
              overflow: hidden;
            }
          ` : ''}
        `}
      </style>
      
      {/* Mobile Overlay */}
      {isMobile && isMobileSidebarVisible && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 45,
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={hideMobileSidebar}
        />
      )}

      <div 
        ref={sidebarRef}
        className="global-sidebar-wrapper" 
        onClick={handleSidebarClick}
        style={{ 
          height: isMobile ? '100vh' : 'calc(100vh - 70px)', 
          position: 'fixed', 
          top: isMobile ? 0 : '70px', 
          left: isMobile ? 'auto' : 0,
          right: isMobile ? 0 : 'auto',
          zIndex: 50,
          cursor: (!isMobile && isCollapsed) ? 'pointer' : 'default',
          transform: isMobile ? (isMobileSidebarVisible ? 'translateX(0)' : 'translateX(100%)') : 'translateX(0)',
          transition: isMobile ? 'transform 0.3s ease-out' : 'all 0.3s ease'
        }}
      >
        <Sidebar
          collapsed={isMobile ? false : isCollapsed}
          width={isMobile ? "320px" : "280px"}
          collapsedWidth={isMobile ? "320px" : "60px"}
          backgroundColor="#000000"
          rootStyles={{
            color: '#ffffff',
            height: '100vh',
            boxShadow: isMobile ? '-2px 0 10px rgba(0,0,0,0.3)' : '2px 0 5px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          {/* Logo/Brand Section */}
          <div 
            className="sidebar-header" 
            onClick={(e) => {
              e.stopPropagation(); // Prevent bubbling to sidebar wrapper
              if (isMobile) {
                hideMobileSidebar();
              } else if (isCollapsed) {
                expandSidebar();
              } else {
                collapseSidebar();
              }
            }}
            style={{
              padding: '20px 15px',
              borderBottom: '1px solid #333333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: (isMobile || !isCollapsed) ? 'space-between' : 'center',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(15, 156, 243, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title={isMobile ? "Close Sidebar" : (isCollapsed ? "Expand Sidebar" : "Collapse Sidebar")}
          >
            {(isMobile || !isCollapsed) ? (
              <>
                <h4 style={{ color: '#ffffff', margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                  gvkEDU
                </h4>
                <FontAwesomeIcon 
                  icon={faTimes} 
                  style={{ 
                    fontSize: '16px', 
                    color: '#ffffff',
                    transition: 'color 0.3s ease'
                  }} 
                />
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <FontAwesomeIcon icon={faBars} style={{ color: '#ffffff', fontSize: '24px' }} />
              </div>
            )}
          </div>

          {/* Menu Items */}
          <Menu
            menuItemStyles={{
              button: ({ level, active }) => ({
                color: active ? '#0f9cf3' : '#ffffff',
                backgroundColor: active ? 'rgba(15, 156, 243, 0.1)' : 'transparent',
                borderRadius: level === 0 ? '0' : '4px',
                margin: level === 0 ? '0' : '2px 8px',
                paddingLeft: level === 0 ? '20px' : '40px',
                '&:hover': {
                  backgroundColor: 'rgba(15, 156, 243, 0.2)',
                  color: '#0f9cf3'
                },
                '&.active': {
                  backgroundColor: 'rgba(15, 156, 243, 0.2)',
                  color: '#0f9cf3',
                  borderLeft: level === 0 ? '3px solid #0f9cf3' : 'none'
                }
              }),
              subMenuContent: {
                backgroundColor: '#111111'
              }
            }}
          >
            {/* Home - Always visible */}
            <MenuItem
              component={<Link to="/" onClick={() => isMobile && hideMobileSidebar()} />}
              icon={<FontAwesomeIcon icon={faHome} />}
              className={isActive('/') ? 'active' : ''}
            >
              Home
            </MenuItem>

            {/* Show auth options for unauthenticated users */}
            {!loggedIn ? (
              <>
                <MenuItem
                  component={<Link to="/login" onClick={() => isMobile && hideMobileSidebar()} />}
                  icon={<FontAwesomeIcon icon={faSignInAlt} />}
                  className={isActive('/login') ? 'active' : ''}
                >
                  Login
                </MenuItem>

              </>
            ) : (
              <>
                {/* All Exams - Top Level */}
                <MenuItem
                  component={<Link to="/exams" onClick={() => isMobile && hideMobileSidebar()} />}
                  icon={<FontAwesomeIcon icon={faGraduationCap} />}
                  className={isActive('/exams') ? 'active' : ''}
                >
                  All Exams
                </MenuItem>

                {/* All Materials - Top Level */}
                <MenuItem
                  component={<Link to="/materials" onClick={() => isMobile && hideMobileSidebar()} />}
                  icon={<FontAwesomeIcon icon={faBookOpen} />}
                  className={isActive('/materials') ? 'active' : ''}
                >
                  Materials
                </MenuItem>

                {/* Mock Test Packages - Top Level */}
                <MenuItem
                  component={<Link to="/mock-tests" onClick={() => isMobile && hideMobileSidebar()} />}
                  icon={<FontAwesomeIcon icon={faClipboardList} />}
                  className={isActive('/mock-tests') ? 'active' : ''}
                >
                  Mock Tests
                </MenuItem>

                {/* Admin - Only visible for admin users */}
                {loggedInUser && loggedInUser.role === 'admin' && (
                  <MenuItem
                    component={<Link to="/admin" onClick={() => isMobile && hideMobileSidebar()} />}
                    icon={<FontAwesomeIcon icon={faUserShield} />}
                    className={isActive('/admin') ? 'active' : ''}
                  >
                    Admin
                  </MenuItem>
                )}
              </>
            )}

          </Menu>

          {/* User Authentication Section - Only show for logged in users */}
          {loggedIn && <UserMenu />}

          {/* Floating Expand Button - Only visible when collapsed on desktop */}
          {!isMobile && isCollapsed && (
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10
            }}>
              <button
                onClick={expandSidebar}
                style={{
                  background: '#ffffff',
                  border: 'none',
                  color: '#1a1a2e',
                  cursor: 'pointer',
                  padding: '12px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(255, 255, 255, 0.4)',
                  width: '48px',
                  height: '48px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateX(-50%) scale(1.15)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.6)';
                  e.target.style.color = '#0f9cf3';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateX(-50%) scale(1)';
                  e.target.style.boxShadow = '0 4px 16px rgba(255, 255, 255, 0.4)';
                  e.target.style.color = '#1a1a2e';
                }}
                aria-label="Expand Menu"
                title="Expand Menu"
              >
                <FontAwesomeIcon icon={faBars} style={{ fontSize: '16px' }} />
              </button>
            </div>
          )}
        </Sidebar>
      </div>
    </>
  );
};

export default GlobalSidebar; 